"use client";
// ReorderableList.tsx
// Mantine v6 + React + TypeScript
// Features: drag & drop (touch friendly), keyboard drag, and Up/Down buttons as fallback
// Install (if not already):
//   pnpm add @mantine/core @mantine/hooks @tabler/icons-react @dnd-kit/core @dnd-kit/sortable @dnd-kit/utilities

import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ActionIcon, Box, Group, Paper, Stack, Text } from "@mantine/core";
import { IconArrowDown, IconArrowUp } from "@tabler/icons-react";
import React, { useMemo } from "react";

// Props
export interface ReorderableListProps<T> {
  items: T[];
  getId: (item: T) => string; // must be stable & unique per item
  render: (item: T, index: number) => React.ReactNode; // how each item displays
  onChange: (next: T[]) => void; // called after reorder
  disabled?: boolean;
  itemRadius?: string | number; // Mantine radius e.g., "md"
}

export default function ReorderableList<T>(props: ReorderableListProps<T>) {
  const { items, getId, render, onChange, disabled, itemRadius = "md" } = props;

  const ids = useMemo(() => items.map(getId), [items, getId]);

  const moveBy = (from: number, delta: number) => {
    const to = from + delta;
    if (to < 0 || to >= items.length) return;
    onChange(arrayMove(items, from, to));
  };

  return (
    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
      <Stack>
        {items.map((item, index) => (
          <SortableRow
            key={getId(item)}
            id={getId(item)}
            index={index}
            isLast={index === items.length - 1}
            disabled={!!disabled}
            onMoveUp={() => moveBy(index, -1)}
            onMoveDown={() => moveBy(index, +1)}
            radius={itemRadius}
          >
            {render(item, index)}
          </SortableRow>
        ))}
      </Stack>
    </SortableContext>
  );
}

// Sortable item row
interface SortableRowProps {
  id: string;
  index: number;
  isLast: boolean;
  disabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  children: React.ReactNode;
  radius?: string | number;
}

function SortableRow({
  id,
  index,
  isLast,
  disabled,
  onMoveUp,
  onMoveDown,
  children,
  radius = "md",
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: "none",
    userSelect: "none",
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      withBorder
      radius={radius}
      style={style}
      aria-roledescription="sortable item"
    >
      <Group noWrap spacing="xs" p="md">
        {/* Content */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fw={500} size="md" c="dimmed" mb={4}>
            #{index + 1}
          </Text>
          <Box>{children}</Box>
        </Box>

        {/* Fallback controls (bigger hit area for touch) */}
        <Group>
          <ActionIcon
            aria-label="Move up"
            size={"xl"}
            variant="light"
            onClick={onMoveUp}
            disabled={disabled || index === 0}
          >
            <IconArrowUp />
          </ActionIcon>
          <ActionIcon
            aria-label="Move down"
            size={"xl"}
            variant="light"
            onClick={onMoveDown}
            disabled={disabled || isLast}
          >
            <IconArrowDown />
          </ActionIcon>
        </Group>
      </Group>
    </Paper>
  );
}
